import type { ReactNode } from "react"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import { useIsEchoesOwner } from "@/store/session"

const DEFAULT_MESSAGE = "Echoesで編集してください"

type EchoesEditGuardProps = {
  /** 無効化対象のアクション要素(ボタン・リンク等) */
  children: ReactNode
  /** ツールチップに表示する誘導文言 */
  message?: string
  /** 幅いっぱいに広げるボタン(block)をラップする場合に true */
  block?: boolean
  /** 外側ラッパーへ付与する追加クラス */
  className?: string
}

/**
 * Echoes連携オーナー(E-08)向けに、物件・部屋・賃借人の作成/編集/削除アクションを
 * 無効化し、「Echoesで編集してください」というツールチップ誘導を表示するラッパー。
 *
 * - 連携していないオーナーには子要素をそのまま描画する。
 * - 連携オーナーには pointer-events を無効化してボタン・リンクいずれの操作も止め、
 *   半透明にして無効状態を視覚的に伝える。UI回避による更新は別途サーバーガード
 *   (blockEchoesOwnerMiddleware)で 403 拒否される。
 */
export function EchoesEditGuard({
  children,
  message = DEFAULT_MESSAGE,
  block = false,
  className,
}: EchoesEditGuardProps) {
  const isEchoesOwner = useIsEchoesOwner()

  if (!isEchoesOwner) {
    return <>{children}</>
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          aria-disabled
          className={cn("cursor-not-allowed", block ? "flex w-full" : "inline-flex", className)}
        >
          <span className={cn("pointer-events-none opacity-50", block && "w-full")}>
            {children}
          </span>
        </span>
      </TooltipTrigger>
      <TooltipContent>{message}</TooltipContent>
    </Tooltip>
  )
}
